import { useEffect, useRef, useState } from 'react';
import type { Event as PolyEvent } from '@polymarket/client';

import { getPolyClient } from '../utils/client';
import {
  extractTokenIds,
  isEventClosed,
  sortEventMarkets,
  type FetchEventRequest,
  type LiveUpdate,
} from '../utils/api';

export type EventIdentifier = { slug: string } | { id: string } | { url: string };

export type UsePolyEventResult = {
  event: PolyEvent | null;
  liveByTokenId: Record<string, LiveUpdate>;
  isLoading: boolean;
  error: unknown;
};

export function usePolyEvent(identifier: EventIdentifier | null): UsePolyEventResult {
  const [event, setEvent] = useState<PolyEvent | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [liveByTokenId, setLiveByTokenId] = useState<Record<string, LiveUpdate>>({});

  const requestSeq = useRef(0);

  const slug = identifier && 'slug' in identifier ? identifier.slug : undefined;
  const id = identifier && 'id' in identifier ? identifier.id : undefined;
  const url = identifier && 'url' in identifier ? identifier.url : undefined;

  useEffect(() => {
    if (!slug && !id && !url) {
      setEvent(null);
      setLiveByTokenId({});
      setError(null);
      return;
    }

    const myId = ++requestSeq.current;
    setIsLoading(true);
    setError(null);

    const request: FetchEventRequest = id ? { id } : url ? { url } : { slug: slug as string };

    getPolyClient()
      .fetchEvent(request)
      .then((next) => {
        if (myId !== requestSeq.current) return;
        setEvent(sortEventMarkets(next, { forceSort: true }));
        setLiveByTokenId({});
        setIsLoading(false);
      })
      .catch((err) => {
        if (myId !== requestSeq.current) return;
        setError(err);
        setIsLoading(false);
      });
  }, [slug, id, url]);

  const tokenKey = event && !isEventClosed(event) ? extractTokenIds([event]).sort().join(',') : '';

  useEffect(() => {
    if (!tokenKey) return;
    const tokenIds = tokenKey.split(',');

    let cancelled = false;
    let handle: { close: () => Promise<void> } | null = null;

    (async () => {
      try {
        const sub = await getPolyClient().subscribe([{ topic: 'market', tokenIds }]);
        if (cancelled) {
          await sub.close();
          return;
        }
        handle = sub;
        for await (const message of sub) {
          if (cancelled) break;
          const update = message as unknown as LiveUpdate;
          const tokenId = update?.payload?.tokenId;
          if (!tokenId) continue;
          setLiveByTokenId((prev) => ({ ...prev, [tokenId]: update }));
        }
      } catch (err) {
        if (!cancelled) console.error('PolyPredictionsEvent subscribe error:', err);
      }
    })();

    return () => {
      cancelled = true;
      if (handle) handle.close().catch(() => {});
    };
  }, [tokenKey]);

  return { event, liveByTokenId, isLoading, error };
}
